/**
 * Execution detail page (/execution?id=…).
 *
 * Data comes from POST /executions/detail via the executionDetail slice, mapped
 * to the ExecutionDetailView view model. Renders the execution summary (headline
 * fields + the executionDetails document), then the payments carried by the
 * execution — found via SearchPayments (paymentIdentifiers=[executionId]) and
 * shown as analytics + a table. The landing-page search bar is kept at the top
 * (without the "Beacon Lookup" title) so another lookup can be run without
 * navigating back.
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import { DetailLink } from 'src/components/common/DetailLink';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchExecutionDetail, clearExecutionDetail, selectExecutionRegion,
} from 'src/store/slices/executionDetail/executionDetailSlice';
import { RegionResults } from 'src/components/common/RegionResults';
import { SearchTabs } from '../search/SearchTabs';
import { ExecutionSummary } from './summary/ExecutionSummary';
import { PaymentAnalytics } from '../payments/PaymentAnalytics';
import { PaymentsTable } from '../payments/PaymentsTable';

export const ExecutionDetailPage = () => {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('id') ?? '';
  const dispatch = useAppDispatch();
  const { region, global } = useAppSelector(state => state.env);
  const {
    data: detail, otherExecutionIds, loading, error, notFound,
    hits, activeRegion, searchedRegions, regionErrors,
  } = useAppSelector(state => state.executionDetail);

  /** Wraps the body in a tab per region holding this execution. */
  const byRegion = (body?: JSX.Element) => (
    <RegionResults
      entity="execution"
      hits={hits}
      activeRegion={activeRegion}
      searchedRegions={searchedRegions}
      regionErrors={regionErrors}
      onSelectRegion={region => dispatch(selectExecutionRegion(region))}
    >
      {body}
    </RegionResults>
  );

  const load = () => {
    if (!executionId) return undefined;
    return dispatch(fetchExecutionDetail(executionId));
  };

  // Load whenever the id changes; abort any in-flight request.
  useEffect(() => {
    const promise = load();
    return () => {
      promise?.abort();
      dispatch(clearExecutionDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, dispatch]);

  // The payments this execution handled live in the same region as the
  // execution, so a global search waits for the lookup to land before it knows
  // where to ask; in a single-region search it is just the selected one. The
  // payments table below fetches (paginated) once this region is known.
  const searchRegion = global ? activeRegion : region;
  // The execution id is both the payment-search identifier and the analytics
  // correlation tag; one memoized array keeps both consumers' effects stable.
  const idList = useMemo(() => [executionId], [executionId]);

  const renderBody = () => {
    if (!executionId) {
      return (
        <Alert type="warning" header="No execution id">
          Provide an execution id, for example /execution?id=&lt;executionId&gt;.
        </Alert>
      );
    }
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Loading execution {executionId}…</Box>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert type="error" header="Failed to load execution" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      );
    }
    if (notFound || !detail) {
      return (
        <SpaceBetween size="l">
          <Alert type="warning" header="Execution not found">
            No execution matched <strong>{executionId}</strong>
            {searchedRegions.length > 1 && ` in any of the ${searchedRegions.length} regions searched`}.
            GetExecutionDetail matches by executionId; use execution search to resolve other
            identifiers.
          </Alert>
          {byRegion()}
        </SpaceBetween>
      );
    }
    return byRegion(
      <SpaceBetween size="l">
        {otherExecutionIds.length > 0 && (
          <Alert type="info" header={`${otherExecutionIds.length} other execution(s) matched this identifier`}>
            <SpaceBetween direction="horizontal" size="xs">
              {otherExecutionIds.map(otherId => (
                <DetailLink key={otherId} to={`/execution?id=${encodeURIComponent(otherId)}`}>
                  {otherId}
                </DetailLink>
              ))}
            </SpaceBetween>
          </Alert>
        )}

        <ExecutionSummary />
        <PaymentAnalytics correlationTags={idList} region={searchRegion} />
        <PaymentsTable ids={idList} region={searchRegion} />
      </SpaceBetween>
    );
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <SearchTabs />
        {renderBody()}
      </SpaceBetween>
    </Box>
  );
};
